import React, { useEffect, useState } from 'react';
import { Database, RefreshCw, Trash2, Calendar, BarChart2, CheckCircle, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface DatabaseItem {
  id: string;
  name: string;
  created_at: string;
  file_name: string;
  file_type: string;
  columns: any;
  row_count: number;
  is_active: boolean;
}

interface Props {
  refreshTrigger?: number;
}

const DatabaseList: React.FC<Props> = ({ refreshTrigger = 0 }) => {
  const { activeDatabase, setActiveDatabase, isLoading: contextLoading, fetchDatabases } = useDatabaseContext();
  const [databases, setDatabases] = useState<DatabaseItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [deletingDb, setDeletingDb] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchDetailedDatabases = async (): Promise<DatabaseItem[]> => {
    try {
      setRefreshing(true);
      setError(null);
      const response = await api.get(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.LIST}`
      );
      const databasesData = response.data || [];
      setDatabases(databasesData);
      setHasInitiallyLoaded(true);
      return databasesData;
    } catch (err) {
      setError('Erreur lors du chargement des bases de données');
      console.error('Erreur:', err);
      setHasInitiallyLoaded(true);
      return [];
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDetailedDatabases();
  }, [refreshTrigger]);

  const handleRefresh = async () => {
    await Promise.all([
      fetchDetailedDatabases(),
      fetchDatabases() // Update context as well
    ]);
  };

  const handleDelete = async (dbName: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la base de données "${dbName}" ?`)) {
      return;
    }

    try {
      // Find the database object to get its ID
      const dbToDelete = databases.find(db => db.name === dbName);
      if (!dbToDelete || !dbToDelete.id) {
        setError('Base de données introuvable');
        return;
      }

      setDeletingDb(dbName);
      await api.delete(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.DELETE(dbToDelete.id)}`
      );
      
      // Refresh both local state and context
      const [updatedDatabases] = await Promise.all([
        fetchDetailedDatabases(),
        fetchDatabases()
      ]);
      
      // Use the returned updated databases list (not the stale state)
      if (activeDatabase?.name === dbName) {
        const remainingDbs = (updatedDatabases || []).filter((db: DatabaseItem) => db.name !== dbName);
        setActiveDatabase(remainingDbs.length > 0 ? remainingDbs[0] : null);
      }
    } catch (err) {
      setError('Erreur lors de la suppression de la base de données');
      console.error('Erreur:', err);
    } finally {
      setDeletingDb(null);
    }
  };

  const handleDatabaseSelect = (dbName: string) => {
    const selectedDb = databases.find(db => db.name === dbName);
    if (selectedDb) {
      setActiveDatabase(selectedDb);
    }
  };

  const handleManageDatabase = (dbName: string) => {
    // Set the database as active before navigating
    const selectedDb = databases.find(db => db.name === dbName);
    if (selectedDb && selectedDb.id) {
      setActiveDatabase(selectedDb);
      // Navigate after setting active database using database ID
      navigate(`/databases/${selectedDb.id}/manage`);
    } else {
      // If database not found, log an error
      console.error(`Database ${dbName} not found in context or missing ID`);
    }
  };

  const isLoading = contextLoading || refreshing;

  // Show spinner only if we're loading AND haven't completed initial load yet
  if (isLoading && !hasInitiallyLoaded) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Chargement des bases de données...</p>
        </div>
      </div>
    );
  }

  // Show custom message if we've loaded but have no databases
  // Check !refreshing instead of !isLoading to ensure we show message even if contextLoading is still true
  if (hasInitiallyLoaded && databases.length === 0 && !refreshing) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Aucune base de données
            </h3>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw 
              size={16} 
              className={`${isLoading ? 'animate-spin' : ''}`}
            />
            Actualiser
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              {error}
            </div>
          </div>
        )}

        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Database className="text-gray-400" size={24} />
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Pour commencer, ajouter une base de données
          </h4>
          <p className="text-gray-500 mb-6">
            Commencez par créer votre première base de données depuis un fichier CSV ou XLSX
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {databases.length > 0 ? `${databases.length} base${databases.length > 1 ? 's' : ''} disponible${databases.length > 1 ? 's' : ''}` : 'Aucune base de données'}
          </h3>
          {databases.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Cliquez sur une base pour la sélectionner
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw 
            size={16} 
            className={`${isLoading ? 'animate-spin' : ''}`}
          />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            {error}
          </div>
        </div>
      )}

      {databases.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Database className="text-gray-400" size={24} />
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Aucune base de données
          </h4>
          <p className="text-gray-500 mb-6">
            Commencez par créer votre première base de données depuis un fichier CSV ou XLSX
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {databases.map((db) => (
            <div
              key={db.name}
              className={`relative bg-white border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md group ${
                activeDatabase?.name === db.name 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleDatabaseSelect(db.name)}
            >
              {/* Active Badge */}
              {activeDatabase?.name === db.name && (
                <div className="absolute -top-2 -right-2 flex items-center gap-1 bg-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                  <CheckCircle size={12} />
                  Active
                </div>
              )}

              {/* Database Icon and Name */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    activeDatabase?.name === db.name ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Database 
                      className={`${
                        activeDatabase?.name === db.name ? 'text-blue-600' : 'text-gray-600'
                      }`} 
                      size={20} 
                    />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">
                      {db.name}
                    </h4>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                      <Calendar size={14} />
                      {new Date(db.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(db.name);
                  }}
                  disabled={deletingDb === db.name}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Supprimer la base de données"
                >
                  <Trash2 
                    size={16}
                    className={`${deletingDb === db.name ? 'animate-pulse' : ''}`}
                  />
                </button>
              </div>

              {/* Database Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-1 bg-green-100 rounded">
                    <BarChart2 className="text-green-600" size={14} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Lignes</div>
                    <div className="font-semibold text-gray-900">
                      {db.row_count.toLocaleString('fr-FR')}
                    </div>
                  </div>
                </div>
                
                             </div>

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleManageDatabase(db.name);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                >
                  <Settings size={14} />
                  Gérer cette base
                </button>
                
                {/* Selection Status */}
                <div className={`text-center text-xs ${
                  activeDatabase?.name === db.name 
                    ? 'text-blue-600 font-medium' 
                    : 'text-gray-500'
                }`}>
                  {activeDatabase?.name === db.name ? '✓ Base sélectionnée' : 'Cliquer pour sélectionner'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DatabaseList;
